import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../utils/i18n';
import { isFallPrepSeason } from '../utils/season';
function isDispatchActive(): boolean {
  // Get current time in Pacific Time
  const now = new Date();
  const pt = new Date(now.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles'
  }));
  const day = pt.getDay(); // 0=Sun, 6=Sat
  const hour = pt.getHours();
  if (day === 0) return false; // Sunday closed
  if (day === 6) return hour >= 6 && hour < 15; // Sat 6AM-3PM
  return hour >= 6 && hour < 17; // Mon-Fri 6AM-5PM
}
export function CtaBanner() {
  const [active, setActive] = useState(isDispatchActive);
  const t = useT();
  // In fall prep season the closing pitch switches from same-day speed to the
  // season's actual driver: repair before the weather makes it worse. The
  // banner also gains a route into the seasonal landing page, which is the
  // only place on the page that diagnoses the problem rather than selling a
  // material.
  const isFall = isFallPrepSeason();
  useEffect(() => {
    const interval = setInterval(() => setActive(isDispatchActive()), 60000);
    return () => clearInterval(interval);
  }, []);
  return <section className="relative py-32 overflow-hidden bg-[#3F3A32]" data-id="element-764" data-mp="168wubxmrole3">
      {/* Hero photo backdrop, darkened 70% so the white headline and the green
              accent word keep their contrast over the texture. */}
      <div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/5JUUQPB3Bh6caPw5U1TsPF/grunge_dirt.jpg)'
    }} aria-hidden="true" data-id="element-765" data-mp="x9tagrfs01yj" />
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" data-id="element-766" data-mp="nk02e0eue4z1" />

      {/* Subtle Grain Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay dark:mix-blend-overlay" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
    }} data-id="element-767" data-mp="9k8qc57vyyno" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-768" data-mp="21jurqv12amea">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-769" data-mp="2z73ip5ju0nno">
          {/* Left: Massive Heading */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7" data-id="element-770" data-mp="2u8s8uutjed5g">
            <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-extrabold text-white tracking-tighter leading-[1.05]" data-id="element-771" data-mp="1du0ewdgqsxu1">
              {t(isFall ? 'cta.fall.headline1' : 'cta.headline1')} <br data-id="element-772" data-mp="3lgk0smsjmr08" />
              <span className="text-[#04A05C]" style={{
              color: "#3BE079"
            }} data-id="element-773" data-mp="3lcw7nor0g6m1">{t(isFall ? 'cta.fall.headline2' : 'cta.headline2')}</span>
            </h2>
          </motion.div>

          {/* Right: Actions & Supporting Text */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.1
        }} className="lg:col-span-5 lg:pl-12 flex flex-col justify-center" data-id="element-774" data-mp="30zrqbt7palq">
            <p className="text-xl text-white/95 font-medium mb-10 leading-relaxed font-sub" data-id="element-775" data-mp="h1k7ez3tyqb0">
              {t(isFall ? 'cta.fall.body' : 'cta.body')}
            </p>

            <div className="flex flex-row flex-wrap gap-3" data-id="element-776" data-mp="2it73vg6hrdev">
              <a href="#" onClick={e => {
              e.preventDefault();
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }} className="group flex items-center justify-center hover:bg-[#17A347] px-6 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 whitespace-nowrap shadow-[0_0_24px_rgba(4,160,92,0.35)] text-[#ffffff] bg-[#04A05C]" data-id="element-777" data-mp="2nr8m5yw3f8pa">
                {t('cta.getQuote')}
                <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" data-id="element-778" data-mp="1xhgbb9o5iiju" />
              </a>

              <a href="tel:206-650-0129" className="flex items-center justify-center hover:bg-stone-100 text-stone-900 px-6 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 whitespace-nowrap bg-[#ffffff]" data-id="element-779" data-mp="19qu5lp1mdad0">
                <Phone className="w-4 h-4 mr-1.5 text-[#04A05C]" data-id="element-780" data-mp="1epnhxe566vbl" />
                (206) 650-0129
              </a>
            </div>

            {isFall && <Link to="/fall-prep" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#22c95c] transition-colors hover:text-white" data-id="element-781" data-mp="qz1bfratdj3p">
                {t('cta.fall.link')}
                <ArrowRight className="w-4 h-4" data-id="element-782" data-mp="2xnaba15b6pbe" />
              </Link>}

            <div className="mt-6 flex items-center text-sm text-white/80 font-medium" data-id="element-783" data-mp="32es0jrxyz1gj">
              <div className={`w-2 h-2 rounded-full mr-2 ${active ? 'bg-white animate-pulse' : 'bg-white/40'}`} data-id="element-784" data-mp="2nq5mdtk2wfqw" />
              {active ? t('cta.dispatchActive') : t('cta.dispatchOffline')}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}